import { arbitrum, base, bsc, bscTestnet, linea, lineaSepolia, mainnet } from "wagmi/chains";

import { ColoredNetworkIconMap } from "@/constants/map";
import { BaseChainInfo } from "@/interfaces/config/evm";
import { Network } from "@/interfaces/network";

export enum CHAIN_ID {
  BASE = base.id,
  BSC = bsc.id,
  LINEA = linea.id,
  ARBITRUM = arbitrum.id,
  ETHEREUM = mainnet.id,
}

export const CHAIN_BY_NETWORK: Record<Network, CHAIN_ID | null> = {
  [Network.NEAR]: null,
  [Network.TON]: null,
  [Network.STELLAR]: null,
  [Network.TON_TESTNET]: null,
  [Network.SOLANA]: null,
  [Network.BASE]: CHAIN_ID.BASE,
  [Network.LINEA]: CHAIN_ID.LINEA,
  [Network.BSC]: CHAIN_ID.BSC,
  [Network.ARBITRUM]: CHAIN_ID.ARBITRUM,
  [Network.ETHEREUM]: CHAIN_ID.ETHEREUM,
};

export const BASE: BaseChainInfo = {
  label: "Base",
  networkType: "L2",
  docs: "https://docs.base.org",
  explorer: "https://basescan.org/",
  infoLink: "https://docs.base.org",
  bridge: "https://bridge.base.org/deposit",
  statusPage: "https://status.base.org/",
  publicNodes: [
    "https://rpc-base.hotdao.ai",
    "https://rpc.ankr.com/base",
    "https://base-rpc.publicnode.com",
    "https://base.llamarpc.com",
    "https://1rpc.io/base",
    "https://base.meowrpc.com",
    "https://base.publicnode.com",
    "https://base-mainnet.public.blastapi.io",
    ...base.rpcUrls.default.http,
  ],
  icon: ColoredNetworkIconMap.BASE,
  chain: base,
};

export const ETHEREUM: BaseChainInfo = {
  label: "Ethereum",
  networkType: "L1",
  bridge: "https://cbridge.celer.network/1/56",
  docs: "https://docs.ethereum.org/",
  explorer: "https://etherscan.io/",
  infoLink: "https://docs.ethereum.org/",
  publicNodes: [
    "https://ethereum.publicnode.com",
    "https://eth.llamarpc.com",
    "https://rpc.ankr.com/eth",
    "https://eth-mainnet.public.blastapi.io",
    "https://cloudflare-eth.com",
    ...mainnet.rpcUrls.default.http,
  ],
  icon: ColoredNetworkIconMap.ETHEREUM,
  chain: mainnet,
};

export const BSC: BaseChainInfo = {
  label: "BSC",
  networkType: "L1",
  bridge: "https://cbridge.celer.network/1/56",
  docs: "https://docs.bnbchain.org/",
  explorer: "https://bscscan.com/",
  infoLink: "https://docs.bnbchain.org/",
  publicNodes: [
    "https://bsc-dataseed.bnbchain.org",
    "https://bsc.publicnode.com",
    "https://binance.llamarpc.com",
    "https://bsc-rpc.publicnode.com",
    "https://bsc.blockpi.network/v1/rpc/public",
    "https://bsc-dataseed1.binance.org",
    "https://bsc-dataseed1.binance.org/",
    "https://bsc-dataseed2.binance.org/",
    "https://bsc-dataseed3.binance.org/",
    "https://bsc-dataseed4.binance.org/",
    "https://bsc-dataseed1.defibit.io/",
    "https://bsc-dataseed2.defibit.io/",
    "https://bsc-dataseed3.defibit.io/",
    "https://bsc-dataseed4.defibit.io/",
    "https://bsc-dataseed1.ninicoin.io/",
    "https://bsc-dataseed2.ninicoin.io/",
    "https://bsc-dataseed3.ninicoin.io/",
    "https://bsc-dataseed4.ninicoin.io/",
    ...bsc.rpcUrls.default.http,
  ],
  icon: ColoredNetworkIconMap.BSC,
  chain: bsc,
};

export const LINEA: BaseChainInfo = {
  label: "Linea",
  networkType: "L2",
  bridge: "https://bridge.linea.build/",
  docs: "https://docs.linea.build/",
  explorer: "https://lineascan.build/",
  infoLink: "https://linea.build/",
  publicNodes: [
    ...linea.rpcUrls.default.http,
    "https://linea.decubate.com",
    "https://linea.blockpi.network/v1/rpc/public",
    "https://linea-mainnet-public.unifra.io",
    "https://linea-mainnet.public.blastapi.io",
  ],
  icon: ColoredNetworkIconMap.LINEA,
  chain: linea,
};

export const ARBITRUM: BaseChainInfo = {
  label: "Arbitrum",
  networkType: "L2",
  bridge: "https://bridge.arbitrum.io/",
  docs: "https://offchainlabs.com/",
  explorer: "https://api.arbiscan.io/api/",
  infoLink: "https://arbiscan.io/",
  publicNodes: [
    ...arbitrum.rpcUrls.default.http,
    "https://arbitrum-one.publicnode.com",
    "https://arbitrum.llamarpc.com",
    "https://arb1.arbitrum.io/rpc",
  ],
  icon: ColoredNetworkIconMap.ARBITRUM,
  chain: arbitrum,
};

export const LINEA_SEPOLIA: BaseChainInfo = {
  label: "Linea_Sepolia",
  networkType: "L2",
  bridge: "https://bridge.linea.build/",
  docs: "https://docs.linea.build/",
  explorer: "https://lineascan.build/",
  infoLink: "https://linea.build/",
  publicNodes: ["https://linea-sepolia.blockpi.network/v1/rpc/public", ...lineaSepolia.rpcUrls.default.http],
  icon: ColoredNetworkIconMap.LINEA,
  chain: lineaSepolia,
};

export const BSC_TESTNET: BaseChainInfo = {
  label: "BSC_TESTNET",
  networkType: "L1",
  bridge: "https://cbridge.celer.network/1/56",
  docs: "https://docs.bnbchain.org/",
  explorer: "https://testnet.bscscan.com/",
  infoLink: "https://docs.bnbchain.org/",
  publicNodes: [
    ...bscTestnet.rpcUrls.default.http,
    "https://bsc-testnet.blockpi.network/v1/rpc/public",
    "https://bsc-testnet-rpc.publicnode.com",
  ],
  icon: ColoredNetworkIconMap.BSC,
  chain: bscTestnet,
};
